import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
  "Richiesta assistenza polizza",
  "Apertura sinistro",
  "Modifica contratto esistente",
  "Richiesta documenti",
  "Informazioni generali",
  "Altro",
];

export default function SupportForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: SUBJECTS[0],
    message: "",
    accept_privacy: false,
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_privacy) {
      setStatus("error");
      setError("Devi spuntare l'accettazione del trattamento dei dati personali per inviare la richiesta.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/support/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject,
        message: form.message.trim(),
        accept_privacy: form.accept_privacy,
      });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: SUBJECTS[0],
        message: "",
        accept_privacy: false,
      });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossibile inviare il messaggio. Riprova più tardi.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="support-success" className="bg-white border border-slate-200 p-10 lg:p-14">
        <div className="w-14 h-14 bg-[color:var(--brand-blue)] flex items-center justify-center mb-6">
          <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={2} />
        </div>
        <h3 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900 mb-4 leading-tight">
          Richiesta inviata correttamente.
        </h3>
        <p className="text-slate-600 leading-relaxed mb-8">
          Grazie per averci contattati. Un nostro consulente ti risponderà direttamente
          all'indirizzo email indicato, di norma entro 24 ore lavorative.
        </p>
        <button data-testid="support-new-btn" onClick={() => setStatus("idle")} className="btn-outline">
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="support-form" className="bg-white border border-slate-200 p-8 lg:p-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Nome e cognome" required>
        <input required minLength={2} maxLength={100} type="text" value={form.name} onChange={update("name")} data-testid="support-name-input" placeholder="Mario Rossi" className="input-90" />
      </Field>
      <Field label="Email" required>
        <input required type="email" value={form.email} onChange={update("email")} data-testid="support-email-input" placeholder="mario.rossi@email.it" className="input-90" />
      </Field>
      <Field label="Telefono">
        <input type="tel" value={form.phone} onChange={update("phone")} data-testid="support-phone-input" placeholder="+39 333 12 34 567" className="input-90" />
      </Field>
      <Field label="Oggetto" required>
        <select required value={form.subject} onChange={update("subject")} data-testid="support-subject-select" className="input-90">
          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>

      <div className="sm:col-span-2">
        <Field label="Messaggio" required>
          <textarea required minLength={5} maxLength={4000} rows={6} value={form.message} onChange={update("message")} data-testid="support-message-textarea" placeholder="Descrivi la tua richiesta nel modo più chiaro possibile..." className="input-90 resize-y min-h-[160px]" />
        </Field>
      </div>

      <label className="sm:col-span-2 flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 cursor-pointer hover:border-[color:var(--brand-blue)]">
        <input type="checkbox" required checked={form.accept_privacy} onChange={update("accept_privacy")} data-testid="support-privacy-checkbox" className="mt-1 w-4 h-4 accent-[color:var(--brand-blue)]" />
        <span className="text-sm text-slate-700 leading-relaxed">
          Ho letto e accetto l'<a href="/reclami#documenti" className="text-[color:var(--brand-blue)] font-semibold underline-offset-2 hover:underline">Informativa Privacy</a> ai sensi del Regolamento UE 2016/679 (GDPR) e autorizzo 90 Brokers Srls al trattamento dei miei dati personali per dare seguito alla mia richiesta. <span className="text-[color:var(--brand-blue)] font-bold">*</span>
        </span>
      </label>

      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <p className="text-xs text-slate-500 leading-relaxed max-w-md">
          I dati inseriti vengono utilizzati esclusivamente per rispondere alla tua richiesta e
          non sono condivisi con terze parti.
        </p>
        <button type="submit" disabled={status === "loading"} data-testid="support-submit-btn" className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
          {status === "loading" ? (<><Loader2 className="w-4 h-4 animate-spin" /> Invio in corso…</>) : (<><Send className="w-4 h-4" /> Invia richiesta</>)}
        </button>
      </div>

      {status === "error" && error && (
        <div data-testid="support-error" className="sm:col-span-2 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
        {label} {required && <span className="text-[color:var(--brand-blue)]">*</span>}
      </span>
      {children}
    </label>
  );
}
